import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import { Plus } from "lucide-react";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import SearchInput from "../../../components/ui/SearchInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import StatusBadge from "../../../components/ui/StatusBadge";
import Skeleton from "../../../components/ui/Skeleton";
import EmptyState from "../../../components/ui/FeedbackStates";
import Modal from "../../../components/ui/Modal";
import TablePagination from "../../../components/common/TablePagination";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import { getSocieties } from "../../../services/societyService";
import { getFlatsBySociety } from "../../../services/flatsService";
import { getCurrentRoleKey } from "../../../services/authService";
import {
  deleteParkingSlot,
  listParkingSlots,
  assignParkingSlot,
  unassignParkingSlot,
  updateParkingSlot,
} from "../../../services/parkingService";
import styles from "./ParkingManagement.module.css";

const ParkingManagement = () => {
  const navigate = useNavigate();
  const currentRoleKey = getCurrentRoleKey();
  const readOnly = currentRoleKey === "society-admin";

  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [filters, setFilters] = useState({
    society_id: "",
    assigned: "",
    slot_type: "",
    search: "",
  });

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSlot, setAssignSlot] = useState(null);
  const [assignFlats, setAssignFlats] = useState([]);
  const [assignFlatId, setAssignFlatId] = useState("");

  const [editSlotOpen, setEditSlotOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [editSlotCode, setEditSlotCode] = useState("");
  const [editSlotType, setEditSlotType] = useState("Car");
  const [editSlotLoading, setEditSlotLoading] = useState(false);

  const loadSocieties = useCallback(async () => {
    setLoadingSocieties(true);
    try {
      const res = await getSocieties();
      setSocieties(Array.isArray(res) ? res : []);
    } finally {
      setLoadingSocieties(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listParkingSlots({
        society_id: filters.society_id || undefined,
        assigned: filters.assigned || undefined,
        slot_type: filters.slot_type || undefined,
        search: filters.search || undefined,
        limit,
        offset,
      });
      setRows(res.slots || []);
      setTotal(Number(res.total ?? 0) || 0);
    } catch (error) {
      console.error("PARKING SLOTS LOAD ERROR:", error);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters.assigned, filters.search, filters.slot_type, filters.society_id, limit, offset]);

  useEffect(() => {
    loadSocieties();
  }, [loadSocieties]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setOffset(0);
  }, [filters.assigned, filters.search, filters.slot_type, filters.society_id, limit]);

  const currentPage = useMemo(() => Math.floor(offset / limit) + 1, [limit, offset]);

  const handleUnassign = async (slotId) => {
    try {
      await unassignParkingSlot(slotId);
      await load();
    } catch (error) {
      console.error("UNASSIGN SLOT ERROR:", error);
    }
  };

  const openAssignModal = async (slot) => {
    const societyId = slot?.society_id;
    if (!societyId) return;

    setAssignSlot(slot);
    setAssignFlatId("");
    setAssignFlats([]);
    setAssignOpen(true);

    if (readOnly) return;

    setAssignLoading(true);

    try {
      const flatsRes = await getFlatsBySociety(String(societyId));
      const list = Array.isArray(flatsRes) ? flatsRes : [];
      setAssignFlats(list);
    } catch (error) {
      console.error("LOAD FLATS ERROR:", error);
      setAssignFlats([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const submitAssign = async () => {
    if (!assignSlot?.slot_id || !assignFlatId) return;
    setAssignLoading(true);
    try {
      await assignParkingSlot(assignSlot.slot_id, { flat_id: assignFlatId });
      setAssignOpen(false);
      await load();
    } catch (error) {
      console.error("ASSIGN SLOT ERROR:", error);
    } finally {
      setAssignLoading(false);
    }
  };

  const openEditSlotModal = (slot) => {
    setEditingSlot(slot);
    setEditSlotCode(slot.slot_code || "");
    setEditSlotType(slot.slot_type || "Car");
    setEditSlotOpen(true);
  };

  const handleSaveEditSlot = async () => {
    if (!editingSlot || !editSlotCode.trim()) return;
    setEditSlotLoading(true);
    try {
      await updateParkingSlot(editingSlot.slot_id, {
        slot_code: editSlotCode.trim().toUpperCase(),
        slot_type: editSlotType,
      });
      setEditSlotOpen(false);
      setEditingSlot(null);
      await load();
    } catch (error) {
      console.error("UPDATE PARKING SLOT ERROR:", error);
    } finally {
      setEditSlotLoading(false);
    }
  };

  const handleDeleteClick = (slot) => {
    setDeleteTarget({
      slot,
      type: "Parking Slot",
      id: slot.slot_id,
      name: slot.slot_code || `Slot #${slot.slot_id}`,
      canDelete: !readOnly,
      actionLabel: "Remove",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.slot) return;
    try {
      await deleteParkingSlot(deleteTarget.slot.slot_id);
      await load();
    } catch (error) {
      console.error("DELETE SLOT ERROR:", error);
    }
  };

  const societyNameById = useMemo(() => {
    const map = new Map();
    societies.forEach((s) => map.set(String(s.society_id), s.society_name));
    return (id) => map.get(String(id)) || String(id || "-");
  }, [societies]);

  return (
    <AdminLayout title="Parking Management">
      <div className={styles.page}>
        <div className={styles.actions}>
          {!readOnly ? (
            <>
              <Button type="button" icon={<Plus size={15} />} onClick={() => navigate("/add-parking-slot")}>
                Create Parking Slot
              </Button>
              <Button variant="outline" type="button" onClick={() => navigate("/generate-parking-slots")}>
                Generate Slots
              </Button>
              <Button variant="outline" type="button" onClick={() => navigate("/assign-parking-slot")}>
                Assign Parking Slot
              </Button>
            </>
          ) : null}
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.headerTop}>
              <h2 className={styles.title}>Slots</h2>
            </div>

            <div className={styles.filtersGrid}>
              <FormField label="Society">
                <SelectDropdown
                  value={filters.society_id}
                  onChange={(e) => setFilters((p) => ({ ...p, society_id: e.target.value }))}
                  disabled={loadingSocieties}
                  placeholder="All societies"
                  options={[
                    { label: "All societies", value: "" },
                    ...societies.map((s) => ({
                      label: s.society_name,
                      value: String(s.society_id),
                    }))
                  ]}
                />
              </FormField>

              <FormField label="Assigned Status">
                <SelectDropdown
                  value={filters.assigned}
                  onChange={(e) => setFilters((p) => ({ ...p, assigned: e.target.value }))}
                  placeholder="All"
                  options={[
                    { label: "All", value: "" },
                    { label: "Assigned", value: "true" },
                    { label: "Unassigned", value: "false" }
                  ]}
                />
              </FormField>

              <FormField label="Slot Type">
                <SelectDropdown
                  value={filters.slot_type}
                  onChange={(e) => setFilters((p) => ({ ...p, slot_type: e.target.value }))}
                  placeholder="All types"
                  options={[
                    { label: "All types", value: "" },
                    { label: "Car (4-Wheeler)", value: "Car" },
                    { label: "Bike (2-Wheeler)", value: "Bike" },
                    { label: "EV Charging", value: "EV" },
                    { label: "Visitor", value: "Visitor" },
                    { label: "Truck / Commercial", value: "Truck" },
                  ]}
                />
              </FormField>

              <FormField label="Search">
                <SearchInput
                  value={filters.search}
                  onChange={(val) => setFilters((p) => ({ ...p, search: typeof val === "string" ? val : (val?.target?.value ?? "") }))}
                  placeholder="Slot code or unit number..."
                  onClear={() => setFilters((p) => ({ ...p, search: "" }))}
                />
              </FormField>
            </div>

          </div>

          {loading ? (
            <div style={{ padding: "24px" }}>
              <Skeleton variant="grid" count={6} />
            </div>
          ) : !rows.length ? (
            <div style={{ padding: "32px 16px" }}>
              <EmptyState
                title="No parking slots found"
                description="No parking slots match the selected filters. You can generate or create slots above."
              />
            </div>
          ) : (
            <div className={styles.slotGrid}>
              {rows.map((r) => {
                const assignedUnit = r.flats?.flat_number || "-";
                const isAssigned = Boolean(r.flat_id);

                return (
                  <div
                    key={r.slot_id}
                    className={[styles.slotCard, isAssigned ? styles.slotCardAssigned : ""].filter(Boolean).join(" ")}
                    onClick={() => openAssignModal(r)}
                  >
                    <div className={styles.slotTop}>
                      <div>
                        <div className={styles.slotCode}>{r.slot_code}</div>
                        <div className={styles.slotMeta}>
                          {r.slot_type || "—"} • {societyNameById(r.society_id)}
                        </div>
                      </div>
                      <StatusBadge
                        status={isAssigned ? "approved" : "neutral"}
                        label={isAssigned ? "Assigned" : "Unassigned"}
                      />
                    </div>

                    <div className={styles.slotBottom}>
                      <div className={styles.slotLabel}>Unit</div>
                      <div className={styles.slotValue}>{assignedUnit}</div>
                    </div>

                    {!readOnly ? (
                      <div className={styles.slotActions} onClick={(e) => e.stopPropagation()}>
                        <Button variant="outline" size="sm" onClick={() => openEditSlotModal(r)}>
                          Edit
                        </Button>
                        {isAssigned ? (
                          <Button variant="outline" size="sm" onClick={() => handleUnassign(r.slot_id)}>
                            Unassign
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => openAssignModal(r)}>
                            Assign
                          </Button>
                        )}
                        <Button variant="danger" size="sm" onClick={() => handleDeleteClick(r)}>
                          Remove
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
                    {!loading && rows.length > 0 && (
            <TablePagination
              totalItems={total}
              page={currentPage}
              limit={limit}
              onPageChange={(newPage) => setOffset((newPage - 1) * limit)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setOffset(0);
              }}
              limitOptions={[5, 10, 25, 50, 100]}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={assignOpen}
        onClose={() => {
          if (assignLoading) return;
          setAssignOpen(false);
        }}
        title={assignSlot ? `Slot ${assignSlot.slot_code}` : "Slot Details"}
      >
        {assignSlot ? (
          <div className={styles.assignModal}>
            <div className={styles.assignHint}>
              Society: <b>{societyNameById(assignSlot.society_id)}</b>
            </div>

            <div className={styles.assignHint}>
              Type: <b>{assignSlot.slot_type || "-"}</b> • Assigned Unit:{" "}
              <b>{assignSlot.flats?.flat_number || "-"}</b>
            </div>

            {!readOnly ? (
              <FormField label="Assign Unit">
                <SelectDropdown
                  value={assignFlatId}
                  onChange={(e) => setAssignFlatId(e.target.value)}
                  disabled={assignLoading || Boolean(assignSlot.flat_id)}
                  placeholder={assignSlot.flat_id ? "Already assigned" : "Select unit..."}
                  options={[
                    ...assignFlats.map((f) => ({
                      label: `Unit ${f.flat_number}`,
                      value: String(f.flat_id),
                    }))
                  ]}
                />
              </FormField>
            ) : null}

            <div className={styles.assignActions}>
              <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={assignLoading}>
                Cancel
              </Button>
              {!readOnly ? (
                assignSlot.flat_id ? (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      setAssignLoading(true);
                      try {
                        await unassignParkingSlot(assignSlot.slot_id);
                        setAssignOpen(false);
                        await load();
                      } finally {
                        setAssignLoading(false);
                      }
                    }}
                    disabled={assignLoading}
                  >
                    {assignLoading ? "Updating..." : "Unassign"}
                  </Button>
                ) : (
                  <Button onClick={submitAssign} disabled={assignLoading || !assignFlatId}>
                    {assignLoading ? "Assigning..." : "Assign Unit"}
                  </Button>
                )
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={editSlotOpen}
        onClose={() => {
          if (editSlotLoading) return;
          setEditSlotOpen(false);
        }}
        title={editingSlot ? `Edit Slot ${editingSlot.slot_code}` : "Edit Parking Slot"}
      >
        {editingSlot ? (
          <div className={styles.assignModal}>
            <FormField label="Slot Code" required>
              <TextInput
                value={editSlotCode}
                onChange={(e) => setEditSlotCode(e.target.value.toUpperCase())}
                placeholder="e.g. C-101, B-005"
                disabled={editSlotLoading}
              />
            </FormField>

            <FormField label="Slot Type" required>
              <SelectDropdown
                value={editSlotType}
                onChange={(e) => setEditSlotType(e.target.value)}
                disabled={editSlotLoading}
                options={[
                  { label: "Car (4-Wheeler)", value: "Car" },
                  { label: "Bike (2-Wheeler)", value: "Bike" },
                  { label: "Truck / Freight", value: "Truck" },
                  { label: "EV Charging", value: "EV" },
                  { label: "Visitor", value: "Visitor" },
                ]}
              />
            </FormField>

            <div className={styles.assignActions}>
              <Button variant="outline" onClick={() => setEditSlotOpen(false)} disabled={editSlotLoading}>
                Cancel
              </Button>
              <Button onClick={handleSaveEditSlot} disabled={editSlotLoading || !editSlotCode.trim()}>
                {editSlotLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Parking Slot"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Remove"}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
};

export default ParkingManagement;


